<template>
  <div class="flex min-h-full flex-1 flex-col justify-center pt-24 sm:px-6 lg:px-8">
    <div class="no-focus-outline sm:mx-auto sm:w-full sm:max-w-md" ref="logoDiv" tabindex="-1">
      <WordmarkLogo class="mx-auto" />
    </div>

    <div class="mt-6 flex flex-col gap-6 sm:mx-auto sm:w-full sm:max-w-sm">
      <p v-if="errorMessage" class="text-[#e05454]">{{ t("msg.error") }}: {{ errorMessage }}</p>

      <!-- Show loading state while checking SSO -->
      <div v-if="isCheckingSso" class="flex justify-center py-4"></div>

      <template v-else-if="useSaml">
        <button
          id="login-sso-button"
          @click="samlRedirect"
          :class="
            twMerge(
              'inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0',
              `theme-${theme ?? 'grape'}${highAccessibility ? '-hc' : ''}`,
            )
          "
        >
          {{ t("msg.sso") }}
        </button>

        <div class="relative">
          <div class="absolute inset-0 flex items-center" aria-hidden="true">
            <div class="w-full border-b border-foreground" />
          </div>
          <div class="relative flex justify-center text-sm font-medium leading-6">
            <span class="bg-background px-6">
              {{ t("msg.or") }}
            </span>
          </div>
        </div>

        <button
          type="button"
          @click="showForm = !showForm"
          :aria-expanded="showForm"
          class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0"
        >
          {{ $t("msg.localLogin") }}
          <ChevronDownIcon v-if="!showForm" class="ml-2 h-5 w-5" />
          <ChevronUpIcon v-if="showForm" class="ml-2 h-5 w-5" />
        </button>
      </template>

      <FormKit
        v-if="!isCheckingSso && (!useSaml || showForm)"
        type="form"
        @submit="handleLogin"
        @submit-invalid="globals.focusFirstError"
        :actions="false"
        class="space-y-2"
      >
        <FormKit
          type="text"
          :classes="{ outer: { 'max-w-[20em]': false } }"
          :label="t('msg.username')"
          name="username"
          id="username"
          validation="required"
          placeholder="john.doe"
        />

        <FormKit
          type="password"
          :classes="{ outer: { 'max-w-[20em]': false } }"
          :label="t('msg.password')"
          name="password"
          id="password"
          validation="required"
          placeholder="••••••••••"
          autocomplete="off"
        />

        <button
          type="submit"
          :class="globals.btnClass(theme, highAccessibility)"
          class="mt-6 flex w-full justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm"
        >
          {{ t("msg.login") }}
        </button>

        <div class="text-right text-sm">
          <a href="/admin">Admin login is here</a>
        </div>
      </FormKit>

      <FormKit
        v-if="!isCheckingSso"
        v-model="kioskMode"
        label="This is a public computer"
        name="kioskMode"
        type="checkbox"
        :classes="{ outer: '!max-w-full flex justify-center' }"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { FormKit } from "@formkit/vue";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/vue/20/solid";
import { useI18n } from "vue-i18n";
import { twMerge } from "tailwind-merge";

import globals from "../globals";
import authService from "../services/auth.service.ts";

import WordmarkLogo from "../components/WordmarkLogo.vue";
import { useAdminStore } from "../pinia/admin.ts";
import { useAuthStore } from "../pinia/auth.ts";
import { REFERRER_QUERY_KEY, useRedirectUrl } from "../utils/useRedirectUrl.js";

const { t } = useI18n();

const adminStore = useAdminStore();
const authStore = useAuthStore();
authStore.fetchTheme();
authStore.fetchHighAccessibility();

const router = useRouter();
const route = useRoute();

const theme = computed(() => authStore.theme?.Accent);
const highAccessibility = computed(() => authStore.theme?.HighAccessibility);
const errorMessage = ref("");
const useSaml = ref(false);
const isCheckingSso = ref(true); // Add loading state
const showForm = ref(false);
const kioskMode = ref(false);

const logoDiv = ref(null);

const { getReferrerUrl } = useRedirectUrl([]);

// Check SSO status immediately on mount
const checkSsoStatus = async () => {
  try {
    const response = await authService.useSAML();
    if (response.status === 200) {
      useSaml.value = true;
    }
  } catch (error) {
    useSaml.value = false;
  } finally {
    isCheckingSso.value = false;
  }
};

const samlRedirect = () => {
  const referrerUrl = getReferrerUrl();
  let url = "api/login/saml?kioskMode=" + kioskMode.value;

  if (referrerUrl) {
    url += "&" + REFERRER_QUERY_KEY + "=" + encodeURIComponent(referrerUrl);
  }

  location.href = url;
};

function triggerButtonClickIfUnfocused() {
  const active = document.activeElement;
  const skipTags = ["INPUT", "TEXTAREA", "BUTTON", "SELECT"];

  const isInteractive = active && (skipTags.includes(active.tagName) || active.isContentEditable);

  if (!isInteractive) {
    const loginButton = document.getElementById("login-sso-button");
    if (loginButton) loginButton.click();
  }
}

function onKeyDown(e) {
  if (e.key === "Enter" && useSaml.value && !isCheckingSso.value) {
    triggerButtonClickIfUnfocused();
  }
}

async function handleLogin(fields, _) {
  const credentials = {
    UserName: fields.username,
    Password: fields.password,
    KioskMode: kioskMode.value,
  };

  try {
    await authStore.login(JSON.stringify(credentials));
    const status = await authStore.fetchStatus({ force: true });
    await authStore.fetchTheme();

    if (status.NewUserDashboard) {
      await router.push({ path: "/home/new", query: route.query });
    } else {
      await router.push({ path: "/home", query: route.query });
    }
  } catch (error) {
    if (error.response) {
      switch (error.response.data["Err"]) {
        case "err.totpVerificationRequired":
          router.push({ name: "TwoFactor", query: route.query });
          break;

        case "err.totpEnrolmentRequired":
          router.push({ name: "TwoFactorOnboard", query: route.query });
          break;

        case "err.unauthorised":
          await adminStore.fetchStatus({ force: true });
          if (adminStore.user.TotpEnabled) {
            if (adminStore.user.AdminTotpVerified) {
              router.push({ name: "AdminTwoFactor" });
            } else {
              router.push({ name: "AdminTwoFactorOnboard" });
            }
          } else {
            router.push({ name: "AdminHome" });
          }
          break;
      }

      errorMessage.value = globals.renderError(t, error);
    }
  }
}

onMounted(async () => {
  logoDiv.value?.focus();

  if (route.query.error) {
    errorMessage.value = t(route.query.error);
  }

  window.addEventListener("keydown", onKeyDown);

  try {
    const user = await authStore.fetchStatus({ force: true });
    if (user) {
      if (user.NewUserDashboard) {
        await router.push({ path: "/home/new", query: route.query });
      } else {
        await router.push({ path: "/home", query: route.query });
      }
    }
  } catch (error) {
    // User not authenticated, stay on login page
  }

  await checkSsoStatus();

  window.addEventListener("keydown", onKeyDown);
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKeyDown);
});
</script>

<style scoped>
.no-focus-outline:focus {
  outline: none;
}

.theme-teal {
  @apply bg-theme-teal hover:bg-theme-teal/90 focus-visible:outline-theme-teal;
}

.theme-teal-hc {
  @apply bg-theme-teal-hc hover:bg-theme-teal-hc/90 focus-visible:outline-theme-teal-hc;
}

.theme-grape {
  @apply bg-theme-grape hover:bg-theme-grape/90 focus-visible:outline-theme-grape;
}

.theme-grape-hc {
  @apply bg-theme-grape-hc hover:bg-theme-grape-hc/90 focus-visible:outline-theme-grape-hc;
}

.theme-iceberg {
  @apply bg-theme-iceberg hover:bg-theme-iceberg/90 focus-visible:outline-theme-iceberg;
}

.theme-iceberg-hc {
  @apply bg-theme-iceberg-hc hover:bg-theme-iceberg-hc/90 focus-visible:outline-theme-iceberg-hc;
}

.theme-rose {
  @apply bg-theme-rose hover:bg-theme-rose/90 focus-visible:outline-theme-rose;
}

.theme-rose-hc {
  @apply bg-theme-rose-hc hover:bg-theme-rose-hc/90 focus-visible:outline-theme-rose-hc;
}

.theme-offsite {
  @apply bg-theme-offsite hover:bg-theme-offsite/90 focus-visible:outline-theme-offsite;
}

.theme-offsite-hc {
  @apply bg-theme-offsite-hc hover:bg-theme-offsite-hc/90 focus-visible:outline-theme-offsite-hc;
}
</style>
